import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventRegistrationModal = ({ isOpen, onClose, eventName }) => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    comments: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/event-registration`, {
        event_name: eventName,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        comments: formData.comments
      });

      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: isRTL 
            ? 'تم إرسال طلبك بنجاح! سنتواصل معك قريباً.'
            : 'Your request has been sent successfully! We will contact you soon.'
        });
        setFormData({ fullName: '', email: '', phone: '', organization: '', comments: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus({ type: '', message: '' });
        }, 3000);
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: isRTL
          ? 'حدث خطأ في إرسال الطلب. يرجى المحاولة مرة أخرى.'
          : 'Error sending request. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-t-lg flex items-center justify-between">
          <h3 className="text-xl font-bold">
            {isRTL ? 'طلب حضور الفعالية' : 'Event Registration Request'}
          </h3>
          <button onClick={onClose} className="hover:bg-orange-700 rounded-full p-1 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={`block text-sm font-semibold mb-1 ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'اسم الفعالية' : 'Event Name'}
            </label>
            <input
              type="text"
              value={eventName}
              disabled
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 ${isRTL ? 'text-right' : ''}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'الاسم الكامل' : 'Full Name'} *
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none ${isRTL ? 'text-right' : ''}`}
              placeholder={isRTL ? 'أدخل اسمك الكامل' : 'Enter your full name'}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'البريد الإلكتروني' : 'Email Address'} *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none ${isRTL ? 'text-right' : ''}`}
              placeholder={isRTL ? 'email@example.com' : 'email@example.com'}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'رقم الهاتف' : 'Phone Number'} *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none ${isRTL ? 'text-right' : ''}`}
              placeholder={isRTL ? '+971 XX XXX XXXX' : '+971 XX XXX XXXX'}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'المنظمة / الجهة' : 'Organization'}
            </label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none ${isRTL ? 'text-right' : ''}`}
              placeholder={isRTL ? 'اسم المنظمة (اختياري)' : 'Organization name (optional)'}
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-1 ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'ملاحظات إضافية' : 'Additional Comments'}
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows="3"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none ${isRTL ? 'text-right' : ''}`}
              placeholder={isRTL ? 'أي ملاحظات أو أسئلة (اختياري)' : 'Any notes or questions (optional)'}
            ></textarea>
          </div>

          {submitStatus.message && (
            <div className={`p-3 rounded-lg ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {submitStatus.message}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Send size={20} />
            {submitting ? (isRTL ? 'جاري الإرسال...' : 'Sending...') : (isRTL ? 'إرسال الطلب' : 'Send Request')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventRegistrationModal;
